/**
 * Operation Queue for handling concurrency in lifecycle operations
 */

type Operation<T> = () => Promise<T>

interface QueuedOperation<T> {
  operation: Operation<T>
  resolve: (value: T) => void
  reject: (error: Error) => void
  cancelled: boolean
}

export class OperationQueue {
  private queue: QueuedOperation<unknown>[] = []
  private processing = false
  private lastOperationTime = 0
  private readonly RAPID_CYCLE_THRESHOLD_MS = 100

  async enqueue<T>(operation: Operation<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const queuedOp: QueuedOperation<T> = {
        operation,
        resolve,
        reject,
        cancelled: false,
      }

      this.queue.push(queuedOp as QueuedOperation<unknown>)
      this.trackTiming()
      this.processQueue()
    })
  }

  cancelPending(): void {
    this.queue.forEach((op) => {
      op.cancelled = true
      op.reject(new Error('Operation cancelled'))
    })
    this.queue = []
  }

  isRapidCycle(): boolean {
    const now = Date.now()
    return now - this.lastOperationTime < this.RAPID_CYCLE_THRESHOLD_MS
  }

  get pendingCount(): number {
    return this.queue.length
  }

  private trackTiming(): void {
    this.lastOperationTime = Date.now()
  }

  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return
    }

    this.processing = true

    while (this.queue.length > 0) {
      const queuedOp = this.queue.shift()

      if (!queuedOp || queuedOp.cancelled) {
        continue
      }

      try {
        const result = await queuedOp.operation()
        if (!queuedOp.cancelled) {
          queuedOp.resolve(result)
        }
      } catch (error) {
        if (!queuedOp.cancelled) {
          queuedOp.reject(error as Error)
        }
      }
    }

    this.processing = false
  }
}
